import { Link, useLocation, useNavigate, useSearchParams } from 'react-router';
import React, { memo, useEffect } from 'react';
import { useMediaQuery } from 'react-responsive';
import { Row, Col, Image, Dropdown, ListGroup, NavDropdown, Button } from 'react-bootstrap';
import SimpleBar from 'simplebar-react';
import { isBooleanFalse, isBooleanTrue, isListNotEmpty, isObjectNotEmpty, isStringNotEmpty, removeEmptyAndNull } from '../utils/objectUtils';
import ROUTES from '../constants/routes';
import { FaBell, FaHome, FaUserCircle } from 'react-icons/fa';
import { NotificationModalButton } from './NotificationModal';
import { FaMessage, FaUserDoctor } from 'react-icons/fa6';
import { Avatar } from '../components/Avatar';
import { toName } from '../utils/stringFormatter';
import { getAffiliateName, getCoveredEntityName, getPartnerName, getProgramNameById } from '../utils/entityUtils';
import { hasAffiliatePermissions, hasCoveredEntityPermissions, hasHelpStaffPermissions, hasMarketingManagerPermissions, hasPartnerPermissions, hasRepPermissions } from '../utils/permissions';
import { v4 as uuid } from 'uuid';
import QuickMenuNavDropdown from './marketing/navbars/QuickMenuNavDropdown';
import { useOrchestrator } from '../redux/ducks/orchestrator';
import { RoleSwitcherModalMenuItem } from './RoleSwitcherModal';
import { fromUtcToDeviceTimeZoneForMessages } from '../utils/dateFormatter';
import NotificationIcon from '../components/Notifications/NotificationIcon';
import MarkAsRead from '../components/Notifications/MarkAsRead';
import AuthService from '../firebase/AuthService';
import { useAuth } from '../redux/ducks/auth';
import { calculateCartSummary, updateCouponCode, useCart } from '../redux/ducks/cart';
import { refreshEncounters, useChat } from '../redux/ducks/chat';
import { refreshNotifications, useNotifications } from '../redux/ducks/notifications';
import { setLead, useAnalytics } from '../redux/ducks/analytics';
import { useDispatch } from 'react-redux';
import LogRocket from 'logrocket';
import { update } from '@intercom/messenger-js-sdk';
import { useDeepCompareEffectNoCheck } from '../hooks/useDeepCompareEffect';
import { getStaffDashboardLabel } from '../utils/staffDashboardUtils';

const QuickMenu = ({ showActionItems = true, showMessages = true, showNotifications = true, showBackToHome = false }) => {
	const isDesktop = useMediaQuery({
		query: '(min-width: 1224px)'
	});

	const isSmallMobile = useMediaQuery({
		query: '(max-width: 400px)'
	});

	//#region useSearchParams

	const [searchParams, setSearchParams] = useSearchParams();

	//#endregion
	//#region useNavigate

	const navigate = useNavigate();

	//#endregion
	//#region useLocation

	const location = useLocation();

	//#endregion
	//#region useDispatch

	const dispatch = useDispatch();

	//#endregion
	//#region goTos

	const goTo = (path) => {
		navigate({ pathname: path, search: searchParams.toString() }, { state: { from: location }, replace: true });
	};

	//#endregion
	//#region useAuth

	const { isVerifiedStaff, intercomUserHash, currentAuthUser, currentAuthUserId, profile, authContext, resetAuth } = useAuth();

	//#endregion
	//#region useOrchestrator

	const { coveredEntities, programs, getOrchestrator, affiliates, partners } = useOrchestrator();

	//#endregion
	//#region useNotifications

	const { notifications, unreadNotificationCount } = useNotifications();

	//#endregion
	//#region useChat

	const { encounters, getProviderDetails, getLastMessage, getLastMessageDateTime, changeActiveEncounter, unreadEncounterMessageCount } = useChat();

	//#endregion
	//#region useCart

	const { cartItems, shippingMethod, coupon, taxRate, clearCart } = useCart();

	//#endregion
	//#region useAnalytics

	const { couponCode } = useAnalytics();

	//#endregion

	const handleOpenChatThread = (item) => {
		changeActiveEncounter(item.id);
		goTo(ROUTES.MY_MESSAGES);
	};

	const signOut = async () => {
		try {
			const success = await AuthService.doSignOut();
			if (isBooleanTrue(success)) {
				await resetAuth();

				await clearCart();
				await dispatch(setLead(null));
				setSearchParams(params => {
					params.delete('lid');
					return params;
				}, { replace: true });

				return true;
			}
		} catch (error) {
			console.log(error.message);
		}

		return false;
	};

	const [expandedMenu, setExpandedMenu] = React.useState(false);

	const CoveredEntityUserDropDown = () => {
		if (isListNotEmpty(authContext.coveredEntityUsers)) {
			let ChildDropDownNav = [];
			authContext.coveredEntityUsers.forEach((coveredEntityUser) => {
				ChildDropDownNav.push({
					id: uuid(),
					header: true,
					header_text: getCoveredEntityName(coveredEntityUser.coveredEntityId, coveredEntities)
				});

				if (isObjectNotEmpty(coveredEntityUser) && hasCoveredEntityPermissions(authContext, coveredEntityUser.coveredEntityId, ['ADMIN']) && isStringNotEmpty(coveredEntityUser.coveredEntityId)) {
					ChildDropDownNav.push({
						id: uuid(),
						menuitem: 'Covered Entity Dashboard',
						link: ROUTES.COVERED_ENTITY + `/${coveredEntityUser.coveredEntityId}`
					})
				}

				if (isObjectNotEmpty(coveredEntityUser) && hasCoveredEntityPermissions(authContext, coveredEntityUser.coveredEntityId, ['CLINICIAN']) && isStringNotEmpty(coveredEntityUser.coveredEntityId) && isStringNotEmpty(coveredEntityUser.userId)) {
					ChildDropDownNav.push({
						id: uuid(),
						menuitem: 'Clinician Dashboard',
						link: ROUTES.LAB + `/${coveredEntityUser.coveredEntityId}` + ROUTES.CLINICIAN + `/${coveredEntityUser.userId}`
					})
				}
			});

			const DropDownNav = {
				id: uuid(),
				menuitem: 'My Covered Entities',
				children: ChildDropDownNav
			};

			return (
				<QuickMenuNavDropdown
					item={DropDownNav}
					onClick={(value) => setExpandedMenu(value)}
				/>
			);
		}

		return null;
	};

	const AffiliateUserDropDown = () => {
		if (isListNotEmpty(authContext.affiliateUsers)) {
			let ChildDropDownNav = [];
			authContext.affiliateUsers.forEach((affiliateUser) => {
				if (isObjectNotEmpty(affiliateUser) && hasAffiliatePermissions(authContext, affiliateUser.affiliateId, ['MANAGER', 'ADMIN']) && isStringNotEmpty(affiliateUser.affiliateId)) {
					ChildDropDownNav.push({
						id: uuid(),
						menuitem: getAffiliateName(affiliateUser.affiliateId, affiliates) + ' Affiliate Dashboard',
						link: ROUTES.AFFILIATE + `/${affiliateUser.affiliateId}`
					})
				}
			});

			const DropDownNav = {
				id: uuid(),
				menuitem: 'My Affiliates',
				children: ChildDropDownNav
			};

			return (
				<QuickMenuNavDropdown
					item={DropDownNav}
					onClick={(value) => setExpandedMenu(value)}
				/>
			);
		}

		return null;
	};

	const PartnerUserDropDown = () => {
		if (isListNotEmpty(authContext.partnerUsers)) {
			let ChildDropDownNav = [];
			authContext.partnerUsers.forEach((partnerUser) => {
				if (isObjectNotEmpty(partnerUser) && hasPartnerPermissions(authContext, partnerUser.partnerId, ['MANAGER', 'ADMIN']) && isStringNotEmpty(partnerUser.partnerId)) {
					ChildDropDownNav.push({
						id: uuid(),
						menuitem: getPartnerName(partnerUser.partnerId, partners) + ' Partner Storefront Dashboard',
						link: ROUTES.PARTNER + `/${partnerUser.partnerId}`
					})
				}
			});

			const DropDownNav = {
				id: uuid(),
				menuitem: 'My Partner Storefronts',
				children: ChildDropDownNav
			};

			return (
				<QuickMenuNavDropdown
					item={DropDownNav}
					onClick={(value) => setExpandedMenu(value)}
				/>
			);
		}

		return null;
	};

	//#region useEffects

	useEffect(() => {
		if (isStringNotEmpty(currentAuthUserId)) {
			LogRocket.identify(currentAuthUserId, removeEmptyAndNull({
				name: currentAuthUser?.displayName,
				email: currentAuthUser?.email ?? null
			}));
		}
	}, [currentAuthUserId, currentAuthUser?.displayName, currentAuthUser?.email]);

	useEffect(() => {
		if (isStringNotEmpty(currentAuthUserId) && isStringNotEmpty(currentAuthUser?.displayName) && isStringNotEmpty(currentAuthUser?.email) && isStringNotEmpty(intercomUserHash)) {
			try {
				update({
					app_id: process.env.REACT_APP_INTERCOM_APP_ID,
					user_id: currentAuthUserId,
					name: currentAuthUser?.displayName ?? null,
					email: currentAuthUser?.email ?? null,
					alignment: 'right',
					vertical_padding: 24
					// created_at: currentAuthUser?.metadata?.createdAt ?? null
				});
			} catch (err) {
				console.log(err);
			}
		}
	}, [currentAuthUserId, currentAuthUser?.displayName, currentAuthUser?.email, intercomUserHash]);

	//#endregion

	return (
		<>
			{/* <DarkLightMode /> */}
			<ListGroup
				as="ul"
				bsPrefix="navbar-nav"
				className="navbar-right-wrap ms-1 d-flex nav-top-wrap"
			>
				{isBooleanTrue(showBackToHome) ? (
					<li className="m-1">
						<Button
							as={Link}
							to={{ pathname: ROUTES.HOME, search: searchParams.toString() }}
							bsPrefix=" "
							className="btn btn-icon navbar-icon rounded-circle"
							title="Back to Home"
						>
							<FaHome size="18" />
						</Button>
					</li>
				) : null}

				{isBooleanTrue(showActionItems) ? (
					<li className="m-1">
						<NotificationModalButton />
					</li>
				) : null}

				{isBooleanTrue(showMessages) ? (
					<Dropdown as="li" className="m-1">
						<Dropdown.Toggle
							as="a"
							bsPrefix=" "
							className={`btn btn-icon navbar-icon rounded-circle ${isSmallMobile ? 'btn-sm' : ''} ${unreadEncounterMessageCount > 0 ? 'indicator indicator-primary' : ''}`}
							id="dropdownMessages"
						>
							<FaUserDoctor />
						</Dropdown.Toggle>
						<Dropdown.Menu
							className={`dashboard-dropdown notifications-dropdown ${isSmallMobile ? 'dropdown-menu-md' : 'dropdown-menu-lg'} dropdown-menu-end mt-3 py-0`}
							aria-labelledby="dropdownMessages"
							align="end"
						>
							<div className="border-bottom px-3 pt-3 pb-3 d-flex justify-content-between align-items-end">
								<span className="h4 mb-0">Doctor Chats</span>
							</div>
							<SimpleBar style={{ maxHeight: '300px' }}>
								<ListGroup variant="flush">
									{encounters.filter(i => i.messages?.length > 0).map((encounter, index) => {
										let otherUser = getProviderDetails(encounter?.providerId ?? null);

										return (
											<ListGroup.Item
												className={index === 0 ? 'bg-light' : ''}
												key={`messages-${index}-${encounter?.id}`}
												style={{ cursor: 'pointer' }}
												onClick={() => { handleOpenChatThread(encounter); }}
											>
												<Row>
													<Col>
														<div className="d-flex">
															{isObjectNotEmpty(otherUser) ? (
																<Avatar
																	size="md"
																	className="rounded-circle"
																	type={otherUser?.imageRefId ? 'image' : 'initial'}
																	imageRefId={otherUser?.imageRefId ?? null}
																	status={otherUser?.status ? otherUser.status.toLowerCase() : 'busy'}
																	alt={toName(otherUser)}
																	name={toName(otherUser)}
																	variant="ready-orange"
																/>
															) : (
																<Avatar
																	size="md"
																	className="rounded-circle"
																	type={'initial'}
																	status={'busy'}
																	alt={encounter?.messageName ?? (encounter?.programId ? getProgramNameById(encounter?.programId, programs) : null) ?? 'Chat Thread'}
																	name={encounter?.messageName ?? (encounter?.programId ? getProgramNameById(encounter?.programId, programs) : null) ?? 'Chat Thread'}
																	variant="ready-orange"
																/>
															)}
															<div className="ms-3">
																<h5 className="fw-bold mb-0">{toName(otherUser) ?? encounter?.messageName ?? (encounter?.programId ? getProgramNameById(encounter?.programId, programs) : null) ?? 'Chat Thread'}</h5>
																<div className="fs-5 mb-0" dangerouslySetInnerHTML={{ __html: getLastMessage(encounter) }} />
																<div className="fs-6 mb-0 text-muted">
																	<span>
																		{getLastMessageDateTime(encounter)}
																	</span>
																</div>
															</div>
														</div>
													</Col>
													<Col xs="auto" className="text-end me-2">
														{encounter.unreadCount > 0 ? (
															<div className="d-flex align-items-center justify-content-center">
																<span className="icon-shape icon-xs text-white bg-danger rounded-circle fw-bold fs-6">
																	{encounter.unreadCount ?? 0}
																</span>
															</div>
														) : null}
													</Col>
												</Row>
											</ListGroup.Item>
										);
									})}
								</ListGroup>
							</SimpleBar>
							<div className="border-top px-3 pt-3 pb-3">
								<Link
									to={{ pathname: ROUTES.MY_MESSAGES, search: searchParams.toString() }}
									className="text-link fw-semibold"
								>
									See all Doctor Chats
								</Link>
							</div>
						</Dropdown.Menu>
					</Dropdown>
				) : null}

				{isBooleanTrue(showNotifications) ? (
					<Dropdown as="li" className="m-1">
						<Dropdown.Toggle
							as="a"
							bsPrefix=" "
							className={`btn btn-icon navbar-icon rounded-circle ${isSmallMobile ? 'btn-sm' : ''} ${unreadNotificationCount > 0 ? 'indicator indicator-primary' : ''}`}
							id="dropdownNotification"
						>
							<FaBell />
						</Dropdown.Toggle>
						<Dropdown.Menu
							show={isDesktop ? true : false}
							className={`dashboard-dropdown notifications-dropdown ${isSmallMobile ? 'dropdown-menu-md' : 'dropdown-menu-lg'} dropdown-menu-end mt-3 py-0`}
							aria-labelledby="dropdownNotification"
							align="end"
						>
							<div className="border-bottom px-3 pt-3 pb-3 d-flex justify-content-between align-items-end">
								<span className="h4 mb-0">Notifications</span>
							</div>
							<SimpleBar style={{ maxHeight: '300px' }}>
								<ListGroup variant="flush">
									{notifications.filter(i => i.type !== 'SILENT').slice(0, 10).map((item, index) => {
										return (
											<ListGroup.Item className={index === 0 ? 'bg-light' : ''} key={`notification-${index}-${item.id}`}>
												<Row>
													<Col>
														<Link className="text-body text-decoration-none" to={item?.cta?.url ? item?.cta?.url : "#"}>
															<div className="d-flex">
																<div className="avatar-md rounded-circle">
																	<NotificationIcon severity={item.severity} type={item.type} />
																</div>
																<div className="ms-3">
																	<h5 className="fw-bold mb-1">{item.subject}</h5>
																	<p className="mb-3">{item.body}</p>
																	<div>
																		{item?.cta?.url && <Button className="m-0 p-0" variant="link">{item?.cta?.label}</Button>}
																	</div>
																	<span className="fs-6 text-muted">
																		<span>
																			<span className="fe fe-thumbs-up text-success me-1"></span>
																			{fromUtcToDeviceTimeZoneForMessages(item.createdAt)}
																		</span>
																	</span>
																</div>
															</div>
														</Link>
													</Col>
													{isBooleanFalse(item.toStaff) && (
														<Col xs="auto" className="text-center me-2">
															<MarkAsRead read={item.read} />
														</Col>
													)}
												</Row>
											</ListGroup.Item>
										);
									})}
								</ListGroup>
							</SimpleBar>
						</Dropdown.Menu>
					</Dropdown>
				) : null}

				{isObjectNotEmpty(authContext.currentAuthUser) ? (
					<Dropdown as="li" className="m-1">
						<Dropdown.Toggle
							as="a"
							bsPrefix=" "
							className="rounded-circle"
							id="dropdownUser"
						>
							<div className={`avatar ${isSmallMobile ? "avatar-sm" : "avatar-md"} avatar-indicators avatar-online`} style={isSmallMobile ? { width: '2.1875rem', height: '2.1875rem' } : {}}>
								{authContext?.currentAuthUser?.photoURL ? (
									<Image
										alt=""
										src={authContext.currentAuthUser.photoURL}
										roundedCircle loading="lazy"
									/>
								) : (
									<FaUserCircle size={isSmallMobile ? "35" : "40"} className="rounded-circle" />
								)}
							</div>
						</Dropdown.Toggle>
						<Dropdown.Menu
							show={isDesktop ? true : false}
							className="dashboard-dropdown dropdown-menu-end mt-4 py-0"
							aria-labelledby="dropdownUser"
							align="end"
						>
							<Dropdown.Header className="mt-3">
								<div className="d-flex">
									<div className="avatar avatar-md avatar-indicators avatar-online">
										{authContext?.currentAuthUser?.photoURL ? (
											<Image
												alt=""
												src={authContext.currentAuthUser.photoURL}
												roundedCircle loading="lazy"
											/>
										) : (
											<FaUserCircle size="40" className="rounded-circle" />
										)}
									</div>
									<div className="ms-3 lh-1">
										<h5 className="mb-1">{isObjectNotEmpty(authContext.currentAuthUser) && isStringNotEmpty(authContext.currentAuthUser.displayName) ? authContext.currentAuthUser.displayName : ""}</h5>
										<p className="mb-0 text-muted">{authContext?.currentAuthUser?.email}</p>
									</div>
								</div>
							</Dropdown.Header>
							<Dropdown.Divider />
							<Dropdown.Item eventKey="2" as={Link} to={{ pathname: ROUTES.ACCOUNT, search: searchParams.toString() }}>
								My Ready<sup>RX</sup>&nbsp;Dashboard
							</Dropdown.Item>

							<CoveredEntityUserDropDown />
							<AffiliateUserDropDown />
							<PartnerUserDropDown />

							{(hasRepPermissions(authContext) || hasHelpStaffPermissions(authContext) || hasMarketingManagerPermissions(authContext)) ? (<NavDropdown.Item as={Link} to={{ pathname: ROUTES.ADMIN, search: searchParams.toString() }}>{getStaffDashboardLabel(authContext)}</NavDropdown.Item>) : null}
							{hasRepPermissions(authContext) && authContext?.isSuperAdmin ? (<NavDropdown.Item as={Link} to={{ pathname: ROUTES.REP_DASHBOARD_PREVIEW, search: searchParams.toString() }}>Rep Dashboard Preview</NavDropdown.Item>) : null}
							{hasRepPermissions(authContext) ? (<NavDropdown.Item onClick={() => { getOrchestrator(true); }}>Refresh Local Storage</NavDropdown.Item>) : null}
							{hasRepPermissions(authContext) ? (<RoleSwitcherModalMenuItem />) : null}

							{/* <Dropdown.Item eventKey="3">
							<i className="fe fe-star me-2"></i> Subscription
						</Dropdown.Item>
						<Dropdown.Item>
							<i className="fe fe-settings me-2"></i> Settings
						</Dropdown.Item> */}
							<Dropdown.Divider />
							<Dropdown.Item onClick={(e) => { e.preventDefault(); signOut(); }} className="mb-3">
								<i className="fe fe-power me-2"></i> Sign Out
							</Dropdown.Item>
						</Dropdown.Menu>
					</Dropdown>
				) : null}
			</ListGroup>
		</>
	);
};

export default memo(QuickMenu);
